import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Bookmark, GitCompare } from 'lucide-react';
import { useGiwahs } from '../../context/GiwahsContext';
import GlobalSearch from '../GlobalSearch';
import AccountMenu from '../AccountMenu';
import { BrandLogo } from '../Brand';

const navItems = [
  { to: '/',           label: 'Home',               variant: 'gold' },
  { to: '/products',   label: 'Products',           variant: 'gold' },
  { to: '/factories',  label: 'Factories',          variant: 'gold' },
  { to: '/intelligence', label: 'Intelligence',     variant: 'blue' },
  { to: '/ai-search',  label: 'AI Search',          variant: 'blue' },
  { to: '/assistant',  label: 'GIWAHS Advisor',     variant: 'blue' },
  { to: '/why-giwahs', label: 'Why GIWAHS Exists',  variant: 'blue' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { savedSuppliers, compareList } = useGiwahs();

  // Cmd/Ctrl+K opens global search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?q=${encodeURIComponent(query.trim())}`);
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="giwahs-navbar"
      className="sticky top-0 z-50 border-b border-hairline bg-[#050505]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[1440px] 2xl:max-w-[1600px] min-[1880px]:max-w-[1760px] min-w-0 items-center justify-between gap-3 px-5 py-3 md:px-8">
        {/* D-081 — logo lock · attached master mark · no pill wrapper */}
        <Link
          to="/"
          data-testid="navbar-logo-link"
          aria-label="GIWAHS — home"
          className="inline-flex shrink-0 items-center transition-transform hover:scale-[1.02]"
        >
          <BrandLogo link={false} height={72} className="md:!h-[88px] navbar-brand-scale" testId="navbar-brand-logo" />
        </Link>

        <nav className="hidden xl:flex min-w-0 items-center gap-1.5">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/ /g, '-')}`}
              className={({ isActive }) =>
                `nav-pill ${n.variant === 'gold' ? 'nav-pill-gold' : 'nav-pill-blue'} ${isActive ? 'active' : ''}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex shrink-0 items-center gap-1.5">
          {/* CB-021 — three-tier desktop actions:
              1280–1535px  compact icon search · Saved/Compare fold into the
                           account area breathing room · full nav pills visible
              1536–1879px  compact icon search + Saved/Compare icons
              ≥1880px      full approved "Search platform… ⌘K" pill */}
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="navbar-global-search-trigger-compact"
            className="giwahs-nav-control flex min-[1880px]:hidden h-10 w-10 items-center justify-center rounded-full"
            title="Search platform (⌘K)"
            aria-label="Search platform"
          >
            <Search size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="navbar-global-search-trigger"
            className="giwahs-nav-control hidden min-[1880px]:flex h-10 items-center gap-2 rounded-full px-4 text-sm w-56 text-left"
          >
            <Search size={14} strokeWidth={1.5} />
            <span className="flex-1">Search platform…</span>
            <span className="giwahs-kbd">⌘K</span>
          </button>
          <Link
            to="/saved"
            data-testid="nav-saved-link"
            className="giwahs-nav-control relative hidden 2xl:flex h-10 w-10 items-center justify-center rounded-full"
            title="Saved suppliers"
          >
            <Bookmark size={14} strokeWidth={1.5} />
            {savedSuppliers.length > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full border bg-[#0B0B0B] px-1 font-mono-data text-[10px]" style={{ borderColor: '#F0C85A', color: '#F0C85A' }}>{savedSuppliers.length}</span>
            )}
          </Link>
          <Link
            to="/compare"
            data-testid="nav-compare-link"
            className="giwahs-nav-control relative hidden 2xl:flex h-10 w-10 items-center justify-center rounded-full"
            title="Compare suppliers"
          >
            <GitCompare size={14} strokeWidth={1.5} />
            {compareList.length > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full border bg-[#0B0B0B] px-1 font-mono-data text-[10px]" style={{ borderColor: '#F0C85A', color: '#F0C85A' }}>{compareList.length}</span>
            )}
          </Link>
          <AccountMenu variant="desktop" />
          <Link
            to="/rfq"
            data-testid="nav-rfq-btn"
            className="btn-giwahs btn-giwahs-blue"
          >
            Request Quote
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-full border xl:hidden"
          style={{ borderColor: 'rgba(200, 169, 107, 0.55)', color: '#C8A96B' }}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} strokeWidth={1.5} /> : <Menu size={18} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="navbar-mobile-drawer" className="border-t border-hairline bg-[#0B0B0B] xl:hidden">
          <button
            onClick={() => { setSearchOpen(true); setOpen(false); }}
            data-testid="navbar-mobile-global-search"
            className="flex w-full items-center gap-2 border-b border-hairline px-5 py-3 text-sm text-platinum-2"
          >
            <Search size={14} strokeWidth={1.5} />
            <span>Search platform…</span>
          </button>
          <div className="flex flex-col">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, '-')}`}
                className={({ isActive }) =>
                  `border-b border-hairline px-5 py-4 font-display text-lg ${isActive ? (n.variant === 'gold' ? 'text-gold' : 'text-blue') : 'text-platinum'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-0 border-b border-hairline">
              <Link to="/saved" onClick={() => setOpen(false)} className="border-r border-hairline px-5 py-4 label-overline text-platinum-2">
                Saved ({savedSuppliers.length})
              </Link>
              <Link to="/compare" onClick={() => setOpen(false)} className="px-5 py-4 label-overline text-platinum-2">
                Compare ({compareList.length})
              </Link>
            </div>
            <div onClick={() => setOpen(false)}>
              <AccountMenu variant="mobile" />
            </div>
            <Link to="/rfq" onClick={() => setOpen(false)} className="btn-giwahs btn-giwahs-primary m-5 text-center">
              Request Quote
            </Link>
          </div>
        </div>
      )}

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default Navbar;
